import { useNavigate } from 'react-router-dom';
import type { PipelineCounts, Stage } from '../../types';

interface PipelineChartProps {
  data: PipelineCounts;
  isLoading?: boolean;
  scope?: 'all' | 'mine';
}

// Stage configuration with labels and colors
const STAGE_CONFIG: Record<Stage, { label: string; color: string; bgColor: string }> = {
  LEAD: {
    label: 'Leads',
    color: 'bg-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
  },
  PROSPECT: {
    label: 'Prospects',
    color: 'bg-purple-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
  },
  CUSTOMER: {
    label: 'Customers',
    color: 'bg-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  CHURNED: {
    label: 'Churned',
    color: 'bg-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
  },
};

// Stage order for display
const STAGE_ORDER: Stage[] = ['LEAD', 'PROSPECT', 'CUSTOMER', 'CHURNED'];

/**
 * PipelineChart - Horizontal bar chart showing people by pipeline stage
 * Each bar is clickable and navigates to the filtered people list
 */
export const PipelineChart = ({ data, isLoading, scope = 'all' }: PipelineChartProps) => {
  const navigate = useNavigate();

  // Calculate total for percentage
  const total = Object.values(data).reduce((sum, count) => sum + count, 0);

  // Handle bar click - navigate to people list with stage filter
  const handleStageClick = (stage: Stage) => {
    navigate(`/people?stage=${stage}&scope=${scope}`);
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Pipeline</h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">{total.toLocaleString()} total</span>
      </div>

      {total === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No people in pipeline yet</p>
          <button
            onClick={() => navigate('/people')}
            className="mt-2 text-sm text-blast-600 hover:text-blast-700"
          >
            Add your first contact →
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {STAGE_ORDER.map((stage) => {
            const config = STAGE_CONFIG[stage];
            const count = data[stage];
            const percentage = total > 0 ? (count / total) * 100 : 0;

            return (
              <button
                key={stage}
                onClick={() => handleStageClick(stage)}
                className="w-full text-left group"
              >
                {/* Label row */}
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
                    {config.label}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                    {count.toLocaleString()}
                  </span>
                </div>

                {/* Bar */}
                <div className={`h-8 rounded-lg ${config.bgColor} overflow-hidden transition-all group-hover:ring-2 group-hover:ring-offset-1 group-hover:ring-blast-300`}>
                  <div
                    className={`h-full ${config.color} rounded-lg transition-all duration-300 flex items-center justify-end pr-2`}
                    style={{ width: `${Math.max(percentage, count > 0 ? 5 : 0)}%` }}
                  >
                    {percentage >= 15 && (
                      <span className="text-xs font-medium text-white">
                        {percentage.toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Legend hint */}
      {total > 0 && (
        <p className="mt-4 text-xs text-gray-400 dark:text-gray-500 text-center">
          Click any stage to view people
        </p>
      )}
    </div>
  );
};
